import { put, race, take } from "redux-saga/effects";
import { openDispatchingModal, updateModalContext } from "actions";

import { FormikProps } from "formik/dist/types";
import { HASH_MODAL_CLOSED, MODAL_CONTEXT } from "constants/actionTypes";
import { ModalTypes } from "constants/navigation";

import { v4 as uuidv4 } from "uuid";
import { openModalLink } from "lib/navigation";

export function modalAction(modalType: ModalTypes, action: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Redux action type
  return (testAction: any) =>
    testAction.type === action && testAction.modalType === modalType;
}

interface ModalConfig<ContextType = unknown, UrlArgsType = unknown> {
  modalType: ModalTypes;
  returnTo?: string;
  context?: ContextType;
  urlArgs?: UrlArgsType;
}

/**
 * Opens a dispatching modal (state driven) and yields until it is closed.
 */
export function* openModalAndWait<ModalT>(modalConfig: ModalConfig<ModalT>) {
  yield put(
    openDispatchingModal(
      modalConfig.modalType,
      window.location.hash,
      modalConfig.context,
      modalConfig.urlArgs,
    ),
  );

  // Wait until:
  return yield race({
    // the modal returns a result
    resultAction: take(
      modalAction(modalConfig.modalType, MODAL_CONTEXT.RESULT.ACTION),
    ),
    // Or the modal is closed without a result
    closedAction: take([
      modalAction(modalConfig.modalType, MODAL_CONTEXT.CLOSE.ACTION),
    ]),
  });
}

/**
 * Opens a HashRoute modal (history.push driven) and yields until it is closed.
 * Requires that the hash modal close path dispatches HASH_MODAL_CLOSED with modalType.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Modal args can be any shape
export function* openHashModalAndWait<TArgs extends Record<string, any>>(
  modalType: ModalTypes,
  args?: TArgs,
  options: { returnTo?: string } = {},
) {
  const returnTo = options.returnTo ?? window.location.hash;

  openModalLink(modalType, {
    ...(args || {}),
    returnTo,
  });

  const closeAction = yield take(modalAction(modalType, HASH_MODAL_CLOSED));

  return closeAction;
}

export interface FormikSnapshot<FormShapeType extends object> {
  values: FormShapeType;
  touched: FormikProps<FormShapeType>["touched"];
  errors: FormikProps<FormShapeType>["errors"];
  submitCount: number;
}

export type SnapshotId = string | null;
export function* storeFormikSnapshot<FormShapeType extends object>(
  modalType: ModalTypes,
  formikSnapshot: FormikSnapshot<FormShapeType>,
) {
  if (formikSnapshot) {
    const id = uuidv4();
    yield put(
      updateModalContext(modalType, {
        formikSnapshot: { [id]: formikSnapshot },
      }),
    );
    return id;
  }
  return null;
}

export function takeFormikSnapshot<FormShapeType extends object>(
  formik: FormikProps<FormShapeType>,
): FormikSnapshot<FormShapeType> {
  return {
    values: formik.values,
    touched: formik.touched,
    errors: formik.errors,
    submitCount: formik.submitCount,
  };
}

export interface ModalContextWithSnapshot<FormShapeType extends object> {
  // Transient values to apply to the form, only retained for a single open session
  values?: Partial<FormShapeType> & { snapshotId?: SnapshotId };
  // Persistent values to apply to the form, retained across multiple open sessions
  persistentValues?: Partial<FormShapeType>;

  formikSnapshot?: {
    [key: SnapshotId]: FormikSnapshot<FormShapeType>;
  };
}

// Transforms an instance into the shape expected by the form, if a transformer is provided
export function transformInstance<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic form shape type
  FormShapeType extends Record<string, any>,
  InstanceType = unknown,
>(
  instance?: InstanceType | null,
  instanceTransformer?: (instance: InstanceType) => Partial<FormShapeType>,
): Partial<FormShapeType> {
  if (instance && instanceTransformer) {
    // We have been provided an existing model instance, which is NOT in the
    // shape the form expects, use the provided transformer to fix that
    return instanceTransformer(instance);
  }
  if (instance) {
    // In this case the model instance is already in the shape of the form
    return instance as Partial<FormShapeType>;
  }
  // return an empty object so that it may be safely spread into the initial values
  return {};
}

// Extracts the saved formik snapshot values from the modal context, if a valid snapshot ID exists.
// This is used to restore form state when returning from a sub-modal workflow.
export function extractSnapshotValues<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic form shape type
  FormShapeType extends Record<string, any>,
>(
  modalCtx?: ModalContextWithSnapshot<FormShapeType> | null,
): Partial<FormShapeType> {
  const snapshotId: SnapshotId | null = modalCtx?.values?.snapshotId ?? null;

  if (snapshotId && modalCtx?.formikSnapshot?.[snapshotId]) {
    return modalCtx.formikSnapshot[snapshotId].values;
  }

  return {};
}

// Extracts transient values from modal context, excluding the snapshotId metadata.
// Transient values are temporary overrides that only apply to a single modal opening,
// typically used when a sub-modal returns data that should be applied to the parent form.
export function extractTransientValues<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic form shape type
  FormShapeType extends Record<string, any>,
>(
  modalCtx?: ModalContextWithSnapshot<FormShapeType> | null,
): Partial<FormShapeType> {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    snapshotId: _ignored,
    ...transientValues
  } = modalCtx?.values ?? {};
  return transientValues as Partial<FormShapeType>;
}

/* Example Usage:

```jsx
const saleModalContext = useSelector(getContextByModalType(ModalTypes.EditSale));
const livestockSale = useSelector(getSaleById(livestockSaleId));

const defaultCreateLivestockSaleFormValues: {
  sale_type: "Saleyard Auction",
  date: getTodayDateString(),
  saleyard_id: defaultSaleyardId,
};

const livestockSaleToFormAdapter = livestockSale=> ({
  ...livestockSale,
  default_buyers_premium: livestockSale.sale_type === "Clearing Sale" ? livestockSale.deploymentSales[0].default_buyers_premium : null,
});

const initialValues = computeInitialValues(
  defaultCreateLivestockSaleFormValues,
  saleModalContext,
  livestockSale,
  livestockSaleToFormAdapter,
);
```
 */
export function computeInitialValues<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic form shape type
  FormShapeType extends Record<string, any>,
  InstanceType = unknown,
>(
  // Base values to use when creating a new instance
  defaults: Partial<FormShapeType>,

  // Modal context which may contain transient, and/or  persistent values and/or a previously saved formik snapshot
  modalCtx?: ModalContextWithSnapshot<FormShapeType> | null,

  // Existing model instance to edit, required when in edit mode
  instance?: InstanceType | null,

  // Optional transformer to convert the model instance into the shape expected by the form
  instanceTransformer?: (instance: InstanceType) => Partial<FormShapeType>,
): Partial<FormShapeType> {
  const formikSnapshotValues = extractSnapshotValues(modalCtx);
  const transientValues = extractTransientValues(modalCtx);
  const isEditMode = Boolean(instance);

  if (isEditMode) {
    const transformedInstance = transformInstance(
      instance,
      instanceTransformer,
    );

    return {
      // Start with the model instance which has been transformed into the shape the form expects
      ...transformedInstance,
      // Restore any saved values from the previous open state. e.g. the form was closed to show a sub-modal
      ...formikSnapshotValues,
      // Apply and new/override values explicitly provided for this invocation of the modal. e.g. the sub modoal returned some new values which we want to work with
      ...transientValues,
    };
  }

  // Apply defaults only if there is no instance (editing existing instance should override defaults)
  const base = defaults || {};
  const persistent = modalCtx?.persistentValues ?? {};

  return {
    // Start with the default create values
    ...base,
    // Persistent values are only applied on create, e.g. re-apply the same agency when using "Create Another"
    ...persistent,
    // Restore any saved values from the previous open state. e.g. the form was closed to show a sub-modal
    ...formikSnapshotValues,
    // Apply and new/override values explicitly provided for this invocation of the modal. e.g. the sub modoal returned some new values which we want to work with
    ...transientValues,
  };
}
